import { useState, useEffect } from 'react'
import { locatariosService, pagosService } from '../services/api'
import { IconSearch, IconUser, IconEdit, IconPlus, IconDownload } from '@tabler/icons-react'

const dividirCorreos = (email) => {
  const partes = (email || '').split(',').map(e => e.trim()).filter(Boolean)
  return [partes[0] || '', partes[1] || '', partes[2] || '']
}

const unirCorreos = (c1, c2, c3) => [c1, c2, c3].map(c => (c || '').trim()).filter(Boolean).join(', ')

export default function Locatarios() {
  const [locatarios, setLocatarios] = useState([])
  const [total, setTotal] = useState(0)
  const [buscar, setBuscar] = useState('')
  const [estado, setEstado] = useState('')
  const [tipoCliente, setTipoCliente] = useState('')
  const [pagina, setPagina] = useState(1)
  const [seleccionado, setSeleccionado] = useState(null)
  const [descargandoExcel, setDescargandoExcel] = useState(false)
  const [estadoCuenta, setEstadoCuenta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [mostrarEditar, setMostrarEditar] = useState(false)
  const [formEdit, setFormEdit] = useState({})
  const [guardando, setGuardando] = useState(false)
  const [mostrarCrear, setMostrarCrear] = useState(false)
  const [formCrear, setFormCrear] = useState({})
  const [creando, setCreando] = useState(false)

  const usuario = JSON.parse(localStorage.getItem('usuario') || '{}')
  const esDirector = usuario.rol === 'director'

  useEffect(() => {
    cargar()
  }, [buscar, estado, tipoCliente, pagina])

  useEffect(() => {
    if (seleccionado) cargarEstadoCuenta(seleccionado.id)
  }, [seleccionado])

  const cargar = async () => {
    setLoading(true)
    try {
      const res = await locatariosService.listar({ buscar, estado, tipoCliente, page: pagina, limit: 20 })
      setLocatarios(res.data.data || [])
      setTotal(res.data.total || 0)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const cargarEstadoCuenta = async (id) => {
    try {
      const res = await pagosService.estadoCuenta(id)
      setEstadoCuenta(res.data)
    } catch {
      setEstadoCuenta(null)
    }
  }

  const abrirEditar = () => {
    if (!esDirector) return
    const [correo1, correo2, correo3] = dividirCorreos(seleccionado.email)
    setFormEdit({
      nombre: seleccionado.nombre || '',
      correo1, correo2, correo3,
      telefono: seleccionado.telefono || '',
      tipoCliente: seleccionado.tipoCliente || 'Residencial',
      estado: seleccionado.estado || 'activo',
      rfc: seleccionado.rfc || '',
      razonSocial: seleccionado.razonSocial || '',
      codigoPostal: seleccionado.codigoPostal || '',
      proyecto: seleccionado.proyecto || '',
      toneladasContratadas: seleccionado.toneladasContratadas ?? '',
      tarifaFijaMensual: seleccionado.tarifaFijaMensual ?? '',
      idSapSecundario: seleccionado.idSapSecundario || ''
    })
    setMostrarEditar(true)
  }

  const guardarEdicion = async () => {
    setGuardando(true)
    try {
      const { correo1, correo2, correo3, ...resto } = formEdit
      const data = { ...resto, email: unirCorreos(correo1, correo2, correo3) }
      const res = await locatariosService.actualizar(seleccionado.id, data)
      setSeleccionado({ ...seleccionado, ...res.data })
      setMostrarEditar(false)
      cargar()
    } catch (err) {
      console.error(err)
      alert('Error al guardar: ' + (err.response?.data?.error || err.message))
    } finally {
      setGuardando(false)
    }
  }

  const descargarExcel = async () => {
    setDescargandoExcel(true)
    try {
      const res = await locatariosService.exportarExcel()
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'residentes-district-cooling-' + new Date().toISOString().slice(0, 10) + '.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error(err)
      alert('Error al generar el Excel')
    } finally {
      setDescargandoExcel(false)
    }
  }

  const abrirCrear = () => {
    setFormCrear({
      subscriptionExternalId: '', nombre: '', correo1: '', correo2: '', correo3: '',
      telefono: '', tipoCliente: 'Residencial', rfc: '', razonSocial: '', codigoPostal: '', proyecto: ''
    })
    setMostrarCrear(true)
  }

  const crearResidente = async () => {
    const { correo1, correo2, correo3, ...resto } = formCrear
    const email = unirCorreos(correo1, correo2, correo3)
    if (!resto.nombre.trim() || !email || !resto.subscriptionExternalId.trim()) {
      alert('Nombre, unidad (ID) y al menos un correo son obligatorios')
      return
    }
    setCreando(true)
    try {
      const res = await locatariosService.crear({ ...resto, email })
      setMostrarCrear(false)
      setSeleccionado(res.data)
      cargar()
    } catch (err) {
      console.error(err)
      alert('Error al crear: ' + (err.response?.data?.error || err.message))
    } finally {
      setCreando(false)
    }
  }

  return (
    <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 120px)' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <IconSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#8A8A7A' }} />
            <input
              className="form-input"
              style={{ paddingLeft: 30 }}
              placeholder="Buscar por nombre, unidad o correo..."
              value={buscar}
              onChange={e => { setBuscar(e.target.value); setPagina(1) }}
            />
          </div>
          <select className="form-select" value={estado} onChange={e => { setEstado(e.target.value); setPagina(1) }}>
            <option value="">Todos</option>
            <option value="activo">Activos</option>
            <option value="suspendido">Suspendidos</option>
            <option value="inactivo">Inactivos</option>
          </select>
          <select className="form-select" value={tipoCliente} onChange={e => { setTipoCliente(e.target.value); setPagina(1) }}>
            <option value="">Residencial + Comercial</option>
            <option value="Residencial">Solo Residencial</option>
            <option value="Comercial">Solo Comercial</option>
          </select>
          <button className="btn" onClick={descargarExcel} disabled={descargandoExcel} style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <IconDownload size={15} /> {descargandoExcel ? 'Generando...' : 'Descargar Excel'}
          </button>
          {esDirector && (
            <button className="btn btn-primary" onClick={abrirCrear} style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
              <IconPlus size={15} /> Nuevo residente
            </button>
          )}
        </div>

        <div className="card" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #D4C9B0', fontSize: 12, color: '#8A8A7A' }}>
            {total} residentes encontrados
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Unidad</th>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Proyecto</th>
                  <th>Toneladas</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#8A8A7A', padding: 24 }}>Cargando...</td></tr>
                )}
                {!loading && locatarios.map(l => (
                  <tr key={l.id} onClick={() => setSeleccionado(l)}
                    style={{ background: seleccionado?.id === l.id ? '#F5EDDD' : '' }}>
                    <td style={{ fontWeight: 500, fontFamily: 'monospace', fontSize: 12 }}>{l.subscriptionExternalId}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{l.nombre}</div>
                      <div className="text-muted">{l.email}</div>
                    </td>
                    <td className="text-muted">{l.tipoCliente || '—'}</td>
                    <td className="text-muted">{l.proyecto || '—'}</td>
                    <td className="text-muted">{l.toneladasContratadas ? `${l.toneladasContratadas} TR` : '—'}</td>
                    <td><span className={`badge badge-${l.estado}`}>{l.estado}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '10px 16px', borderTop: '1px solid #D4C9B0', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-sm" onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1}>← Anterior</button>
            <span style={{ fontSize: 12, color: '#8A8A7A', alignSelf: 'center' }}>Página {pagina}</span>
            <button className="btn btn-sm" onClick={() => setPagina(p => p + 1)} disabled={locatarios.length < 20}>Siguiente →</button>
          </div>
        </div>
      </div>

      {seleccionado && (
        <div className="card" style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: '#F5EDDD', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 500, color: '#8A6930', margin: '0 auto 10px' }}>
              {seleccionado.subscriptionExternalId?.slice(0, 2)}
            </div>
            <div style={{ fontWeight: 500, fontSize: 14 }}>{seleccionado.nombre}</div>
            <div className="text-muted">{seleccionado.subscriptionExternalId}</div>
            <div style={{ marginTop: 8, display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
              <span className={`badge badge-${seleccionado.estado}`}>{seleccionado.estado}</span>
              <span className="badge" style={{ background: '#F5EDDD' }}>{seleccionado.tipoCliente || 'Residencial'}</span>
            </div>
            {esDirector && (
              <button className="btn btn-sm" onClick={abrirEditar} style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: '100%' }}>
                <IconEdit size={13} /> Editar residente
              </button>
            )}
          </div>

          <div>
            <div style={{ fontSize: 11, color: '#8A8A7A', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Contacto</div>
            {(seleccionado.email || '').split(',').map(e => e.trim()).filter(Boolean).map((e, i) => (
              <div key={i} style={{ fontSize: 12, marginBottom: 4 }}>{e}</div>
            ))}
            {!seleccionado.email && <div style={{ fontSize: 12, marginBottom: 4 }}>—</div>}
            <div style={{ fontSize: 12, color: '#8A8A7A' }}>{seleccionado.telefono || 'Sin teléfono'}</div>
          </div>

          <div>
            <div style={{ fontSize: 11, color: '#8A8A7A', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Contrato</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '5px 0', borderBottom: '1px solid #E8E0D0' }}>
              <span style={{ color: '#8A8A7A' }}>Toneladas</span>
              <span style={{ fontWeight: 500 }}>{seleccionado.toneladasContratadas || '—'} TR</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '5px 0', borderBottom: '1px solid #E8E0D0' }}>
              <span style={{ color: '#8A8A7A' }}>Tarifa fija</span>
              <span style={{ fontWeight: 500 }}>${seleccionado.tarifaFijaMensual?.toLocaleString('es-MX') || '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '5px 0', borderBottom: '1px solid #E8E0D0' }}>
              <span style={{ color: '#8A8A7A' }}>RFC</span>
              <span style={{ fontWeight: 500 }}>{seleccionado.rfc || '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '5px 0' }}>
              <span style={{ color: '#8A8A7A' }}>Proyecto</span>
              <span style={{ fontWeight: 500 }}>{seleccionado.proyecto || '—'}</span>
            </div>
          </div>

          {estadoCuenta && (
            <div>
              <div style={{ fontSize: 11, color: '#8A8A7A', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Estado de cuenta</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ background: '#F5EDDD', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: 10, color: '#8A8A7A' }}>Deuda</div>
                  <div style={{ fontSize: 16, fontWeight: 500, color: estadoCuenta.resumen.totalPendiente > 0 ? '#993C1D' : '#3B6D11' }}>
                    ${estadoCuenta.resumen.totalPendiente?.toLocaleString('es-MX')}
                  </div>
                </div>
                <div style={{ background: '#F5EDDD', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: 10, color: '#8A8A7A' }}>Tickets</div>
                  <div style={{ fontSize: 16, fontWeight: 500, color: estadoCuenta.resumen.ticketsAbiertos > 0 ? '#993C1D' : '#1C1C1A' }}>
                    {estadoCuenta.resumen.ticketsAbiertos}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {mostrarEditar && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div className="card" style={{ width: 500, padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 18, marginBottom: 4 }}>Editar residente</div>
            <div className="text-muted" style={{ marginBottom: 16 }}>{seleccionado.subscriptionExternalId}</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Nombre</label>
                <input className="form-input" value={formEdit.nombre} onChange={e => setFormEdit({ ...formEdit, nombre: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Teléfono</label>
                <input className="form-input" value={formEdit.telefono} onChange={e => setFormEdit({ ...formEdit, telefono: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Correo 1 (principal)</label>
                <input className="form-input" value={formEdit.correo1} onChange={e => setFormEdit({ ...formEdit, correo1: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Correo 2 (opcional)</label>
                <input className="form-input" value={formEdit.correo2} onChange={e => setFormEdit({ ...formEdit, correo2: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Correo 3 (opcional)</label>
                <input className="form-input" value={formEdit.correo3} onChange={e => setFormEdit({ ...formEdit, correo3: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Tipo de cliente</label>
                <select className="form-input" value={formEdit.tipoCliente} onChange={e => setFormEdit({ ...formEdit, tipoCliente: e.target.value })}>
                  <option value="Residencial">Residencial</option>
                  <option value="Comercial">Comercial</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Estado</label>
                <select className="form-input" value={formEdit.estado} onChange={e => setFormEdit({ ...formEdit, estado: e.target.value })}>
                  <option value="activo">Activo</option>
                  <option value="suspendido">Suspendido</option>
                  <option value="inactivo">Inactivo</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Proyecto</label>
                <input className="form-input" value={formEdit.proyecto} onChange={e => setFormEdit({ ...formEdit, proyecto: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">RFC</label>
                <input className="form-input" value={formEdit.rfc} onChange={e => setFormEdit({ ...formEdit, rfc: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Razón social</label>
                <input className="form-input" value={formEdit.razonSocial} onChange={e => setFormEdit({ ...formEdit, razonSocial: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Código postal</label>
                <input className="form-input" value={formEdit.codigoPostal} onChange={e => setFormEdit({ ...formEdit, codigoPostal: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">ID SAP secundario</label>
                <input className="form-input" value={formEdit.idSapSecundario} onChange={e => setFormEdit({ ...formEdit, idSapSecundario: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Toneladas contratadas</label>
                <input className="form-input" type="number" step="0.01" value={formEdit.toneladasContratadas} onChange={e => setFormEdit({ ...formEdit, toneladasContratadas: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Tarifa fija mensual</label>
                <input className="form-input" type="number" step="0.01" value={formEdit.tarifaFijaMensual} onChange={e => setFormEdit({ ...formEdit, tarifaFijaMensual: e.target.value })} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn" onClick={() => setMostrarEditar(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardarEdicion} disabled={guardando}>
                {guardando ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {mostrarCrear && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div className="card" style={{ width: 500, padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 18, marginBottom: 4 }}>Nuevo residente</div>
            <div className="text-muted" style={{ marginBottom: 16 }}>Nombre, unidad (ID) y al menos un correo son obligatorios</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Unidad / ID (ej. CA-0601)</label>
                <input className="form-input" value={formCrear.subscriptionExternalId} onChange={e => setFormCrear({ ...formCrear, subscriptionExternalId: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Nombre</label>
                <input className="form-input" value={formCrear.nombre} onChange={e => setFormCrear({ ...formCrear, nombre: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Teléfono</label>
                <input className="form-input" value={formCrear.telefono} onChange={e => setFormCrear({ ...formCrear, telefono: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Tipo de cliente</label>
                <select className="form-input" value={formCrear.tipoCliente} onChange={e => setFormCrear({ ...formCrear, tipoCliente: e.target.value })}>
                  <option value="Residencial">Residencial</option>
                  <option value="Comercial">Comercial</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Correo 1 (principal)</label>
                <input className="form-input" value={formCrear.correo1} onChange={e => setFormCrear({ ...formCrear, correo1: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Correo 2 (opcional)</label>
                <input className="form-input" value={formCrear.correo2} onChange={e => setFormCrear({ ...formCrear, correo2: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Correo 3 (opcional)</label>
                <input className="form-input" value={formCrear.correo3} onChange={e => setFormCrear({ ...formCrear, correo3: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Proyecto</label>
                <input className="form-input" value={formCrear.proyecto} onChange={e => setFormCrear({ ...formCrear, proyecto: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">RFC</label>
                <input className="form-input" value={formCrear.rfc} onChange={e => setFormCrear({ ...formCrear, rfc: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Razón social</label>
                <input className="form-input" value={formCrear.razonSocial} onChange={e => setFormCrear({ ...formCrear, razonSocial: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Código postal</label>
                <input className="form-input" value={formCrear.codigoPostal} onChange={e => setFormCrear({ ...formCrear, codigoPostal: e.target.value })} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn" onClick={() => setMostrarCrear(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={crearResidente} disabled={creando}>
                {creando ? 'Creando...' : 'Crear residente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
